import React, { useState, useEffect } from "react";
import createInstance from "../../../api/axiosinstance";
import { toast } from "react-toastify";
import Loader from "../../Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material"; // Assuming bootstrap for consistency, adjust if needed

const ViewModal = ({
  isOpen,
  onClose,
  title,
  feedback,
  rating,
  candidateId,
  interview,
  onUpdate,
  isReadOnly,
}) => {
  const aiDishaApi = createInstance("DISHA");
  const [candidateDetails, setCandidateDetails] = useState({});
  const [editableFields, setEditableFields] = useState({
    interview_panel: "",
    meeting_link: "",
    round: "",
    interview_duration: "",
    reschedule: "Yes",
    key_notes: "",
    select_interviewer: "",
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [meetingLink, setMeetingLink] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingZoom, setIsGeneratingZoom] = useState(false);
  const [technologists, setTechnologists] = useState([]);
  const [managers, setManagers] = useState([]);
  const [hrs, setHrs] = useState([]);
  const [interviewerList, setInterviewerList] = useState([]);
  const [tpInterviewers, setTpInterviewers] = useState([]);
  const [selectedTpInterviewer, setSelectedTpInterviewer] = useState("");
  const [hasChangedDate, setHasChangedDate] = useState(false);
  const [hasChangedTime, setHasChangedTime] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const organizationId = user?.organizationId;
  const hrId = user?.orgUserId;

  const getCurrentDateTime = () => {
    const now = new Date();
    return now;
  };

  useEffect(() => {
    if (candidateId) {
      getCandidateDetails();
    }
  }, [candidateId]);

  useEffect(() => {
    if (isOpen) {
      getInterviewers();
      getAllTpInterviewers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (editableFields.round && editableFields.select_interviewer) {
      updateInterviewerList(
        editableFields.round,
        editableFields.select_interviewer
      );
    }
  }, [
    editableFields.round,
    editableFields.select_interviewer,
    technologists,
    managers,
    hrs,
  ]);

  const updateInterviewerList = (round, selectInterviewer) => {
    if (selectInterviewer === "Third Party Interviewer") {
      setInterviewerList([]); // Use separate TP list
      return;
    }
    if (round === "Technical Round 1" || round === "Technical Round 2") {
      setInterviewerList(technologists);
    } else if (round === "Managerial") {
      setInterviewerList(managers);
    } else if (round === "HR") {
      setInterviewerList(hrs);
    } else {
      setInterviewerList([]);
    }
  };

  const getInterviewers = async () => {
    setIsLoading(true);
    try {
      const response = await aiDishaApi.get(
        `/get_interviewers?org_id=${organizationId}`
      );
      const interviewers = response.data;
      setTechnologists(interviewers["Technical"] || []);
      setHrs(interviewers["HR"] || []);
      setManagers(interviewers["Managerial"] || []);
      if (editableFields.round && editableFields.select_interviewer) {
        updateInterviewerList(
          editableFields.round,
          editableFields.select_interviewer
        );
      }
    } catch (error) {
      console.log("Error fetching interviewers", error);
      toast.error("Failed to fetch interviewers list");
    } finally {
      setIsLoading(false);
    }
  };

  const getAllTpInterviewers = async () => {
    try {
      const response = await aiDishaApi.get(
        "/get-all-interviewer"
      );
      const users = response.data["active_users"] || [];
      setTpInterviewers(users.filter((u) => u.role === "TP-Interviewer"));
    } catch (error) {
      console.error("Error fetching TP interviewers:", error);
      toast.error("Failed to fetch TP Interviewers. Please try again.");
    }
  };

  const getCandidateDetails = async () => {
    if (!candidateId) return;
    await aiDishaApi
      .get(`/get-candidates-by-candidate-id?candidate_id=${candidateId}`)
      .then((response) => {
        setCandidateDetails(response.data);
      })
      .catch((error) => {
        console.log("Error fetching candidate performance details", error);
      });
  };

  // Auto-generate Zoom link only when both date and time are user-changed
  useEffect(() => {
    const generateZoomLink = async () => {
      if (
        !isReadOnly &&
        selectedDate &&
        selectedTime &&
        hasChangedDate &&
        hasChangedTime
      ) {
        setIsGeneratingZoom(true);
        try {
          const formattedDate = `${selectedDate.getFullYear()}-${String(
            selectedDate.getMonth() + 1
          ).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(
            2,
            "0"
          )}`;
          const formattedTime = `${String(selectedTime.getHours()).padStart(
            2,
            "0"
          )}:${String(selectedTime.getMinutes()).padStart(2, "0")}`;
          const response = await aiDishaApi.post("/zoom-meeting-creator/", {
            date: formattedDate,
            time: formattedTime,
          });
          setMeetingLink(response.data["Join URL"]);
          setMeetingId(String(response.data["Meeting ID"]));
          toast.success("Zoom meeting link generated successfully");
          setEditableFields((prev) => ({
            ...prev,
            meeting_link: response.data["Join URL"],
          }));
        } catch (error) {
          console.error("Error generating Zoom link:", error);
          toast.error("Failed to generate Zoom meeting link");
          setMeetingLink("");
          setMeetingId("");
          setEditableFields((prev) => ({ ...prev, meeting_link: "" }));
        } finally {
          setIsGeneratingZoom(false);
        }
      } else {
        // Set to existing or clear
        if (selectedDate && selectedTime) {
          setMeetingLink(interview?.meeting_link || "");
          setMeetingId(interview?.zoom_meeting_id || "");
          setEditableFields((prev) => ({
            ...prev,
            meeting_link: interview?.meeting_link || "",
          }));
        } else {
          setMeetingLink("");
          setMeetingId("");
          setEditableFields((prev) => ({ ...prev, meeting_link: "" }));
        }
      }
    };
    generateZoomLink();
  }, [
    selectedDate,
    selectedTime,
    hasChangedDate,
    hasChangedTime,
    isReadOnly,
    interview,
  ]);

  useEffect(() => {
    if (isOpen && interview) {
      let dateObj = null;
      let timeObj = null;
      let dateTimeField =
        interview.round === "Technical Round 2"
          ? interview["Technical Round 2 interview_date_and_time"] ||
            interview.interview_date_and_time
          : interview.interview_date_and_time;
      if (dateTimeField) {
        try {
          const parsedDate =
            dateTimeField.includes("Z") || dateTimeField.includes("+")
              ? new Date(dateTimeField)
              : new Date(dateTimeField + "Z");
          if (!isNaN(parsedDate.getTime())) {
            // Convert UTC to IST for display
            const istDate = new Date(
              parsedDate.getTime() + 5.5 * 60 * 60 * 1000
            );
            dateObj = new Date(
              istDate.getFullYear(),
              istDate.getMonth(),
              istDate.getDate()
            );
            timeObj = new Date(istDate.getTime());
            setSelectedDate(dateObj);
            setSelectedTime(timeObj);
          } else {
            console.error("Invalid interview date:", dateTimeField);
            setSelectedDate(null);
            setSelectedTime(null);
          }
        } catch (e) {
          console.error("Error parsing date:", e);
          setSelectedDate(null);
          setSelectedTime(null);
        }
      } else {
        setSelectedDate(null);
        setSelectedTime(null);
      }

      // Set existing meeting details
      setMeetingLink(interview.meeting_link || "");
      setMeetingId(interview.zoom_meeting_id || "");

      setEditableFields({
        interview_panel: interview.interview_panel || "",
        meeting_link: interview.meeting_link || "",
        round: interview.round || "",
        interview_duration: interview.interview_duration || "",
        reschedule: interview.Reschedule || "Yes",
        key_notes: interview.key_notes || interview.keynotes || "",
        select_interviewer: interview.select_interviewer || "",
      });

      // Set TP interviewer if applicable
      if (
        interview.select_interviewer === "Third Party Interviewer" &&
        interview.interview_panel
      ) {
        setSelectedTpInterviewer(interview.interview_panel);
      }

      if (interview.round && interview.select_interviewer) {
        updateInterviewerList(interview.round, interview.select_interviewer);
      }
    }
  }, [isOpen, interview]);

  const handleClose = () => {
    setCandidateDetails({});
    setSelectedDate(null);
    setSelectedTime(null);
    setMeetingLink("");
    setMeetingId("");
    setHasChangedDate(false);
    setHasChangedTime(false);
    setEditableFields({
      interview_panel: "",
      meeting_link: "",
      round: "",
      interview_duration: "",
      reschedule: "Yes",
      key_notes: "",
      select_interviewer: "",
    });
    setSelectedTpInterviewer("");
    onClose();
  };

  const handleInputChange = (e) => {
    if (isReadOnly) return;
    const { name, value } = e.target;
    setEditableFields((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "round" || name === "select_interviewer") {
      setEditableFields((prev) => ({
        ...prev,
        interview_panel: "",
      }));
      setSelectedTpInterviewer("");
    }
  };

  const handleSelectInterviewer = (panelist_id) => {
    if (isReadOnly) return;
    setEditableFields((prev) => ({
      ...prev,
      interview_panel: panelist_id,
    }));
  };

  const handleSelectTpInterviewer = (user_id) => {
    if (isReadOnly) return;
    setSelectedTpInterviewer(user_id);
    setEditableFields((prev) => ({
      ...prev,
      interview_panel: user_id,
    }));
  };

  const validateForm = () => {
    if (isReadOnly) return true;
    if (!candidateId.trim()) {
      toast.error("Candidate ID cannot be empty");
      return false;
    }
    if (!editableFields.round.trim()) {
      toast.error("Round cannot be empty");
      return false;
    }
    if (!editableFields.select_interviewer.trim()) {
      toast.error("Select Interviewer Type cannot be empty");
      return false;
    }
    if (!editableFields.key_notes.trim()) {
      toast.error("Key notes cannot be empty");
      return false;
    }
    const selectedPanel =
      editableFields.select_interviewer === "Third Party Interviewer"
        ? selectedTpInterviewer
        : editableFields.interview_panel;
    if (!selectedPanel.trim()) {
      toast.error("Please select an Interviewer");
      return false;
    }
    if (!selectedDate || !selectedTime) {
      toast.error("Interview Date and Time cannot be empty");
      return false;
    }
    if (!meetingLink.trim()) {
      toast.error(
        "Meeting Link cannot be empty. Please select date and time first."
      );
      return false;
    }
    if (!meetingId.trim()) {
      toast.error(
        "Meeting ID cannot be empty. Please select date and time first."
      );
      return false;
    }
    if (
      !editableFields.interview_duration.trim() ||
      isNaN(editableFields.interview_duration) ||
      editableFields.interview_duration <= 0
    ) {
      toast.error("Please enter a valid interview duration in minutes");
      return false;
    }
    const combinedDateTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      selectedTime.getHours(),
      selectedTime.getMinutes()
    );
    const now = new Date();
    if (combinedDateTime <= now) {
      toast.error("Please select a future date and time");
      return false;
    }
    return true;
  };

  const handleReset = () => {
    if (isReadOnly) return;
    if (interview) {
      let dateObj = null;
      let timeObj = null;
      let dateTimeField =
        interview.round === "Technical Round 2"
          ? interview["Technical Round 2 interview_date_and_time"] ||
            interview.interview_date_and_time
          : interview.interview_date_and_time;
      if (dateTimeField) {
        try {
          const parsedDate =
            dateTimeField.includes("Z") || dateTimeField.includes("+")
              ? new Date(dateTimeField)
              : new Date(dateTimeField + "Z");
          if (!isNaN(parsedDate.getTime())) {
            const istDate = new Date(
              parsedDate.getTime() + 5.5 * 60 * 60 * 1000
            );
            dateObj = new Date(
              istDate.getFullYear(),
              istDate.getMonth(),
              istDate.getDate()
            );
            timeObj = new Date(istDate.getTime());
            setSelectedDate(dateObj);
            setSelectedTime(timeObj);
          } else {
            setSelectedDate(null);
            setSelectedTime(null);
          }
        } catch (e) {
          console.error("Error parsing date on reset:", e);
          setSelectedDate(null);
          setSelectedTime(null);
        }
      } else {
        setSelectedDate(null);
        setSelectedTime(null);
      }

      setMeetingLink(interview.meeting_link || "");
      setMeetingId(interview.zoom_meeting_id || "");

      setEditableFields({
        interview_panel: interview.interview_panel || "",
        meeting_link: interview.meeting_link || "",
        round: interview.round || "",
        interview_duration: interview.interview_duration || "",
        reschedule: interview.Reschedule || "Yes",
        key_notes: interview.key_notes || interview.keynotes || "",
        select_interviewer: interview.select_interviewer || "",
      });

      if (
        interview.select_interviewer === "Third Party Interviewer" &&
        interview.interview_panel
      ) {
        setSelectedTpInterviewer(interview.interview_panel);
      }

      if (interview.round && interview.select_interviewer) {
        updateInterviewerList(interview.round, interview.select_interviewer);
      }

      // Reset change flags
      setHasChangedDate(false);
      setHasChangedTime(false);
    }
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    if (!validateForm()) return;
    if (!interview) return;
    setIsLoading(true);
    try {
      // Convert IST to UTC by subtracting 5.5 hours
      const combinedDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        selectedTime.getHours(),
        selectedTime.getMinutes(),
        0
      );
      const utcDateTime = new Date(
        combinedDateTime.getTime() - 5.5 * 60 * 60 * 1000
      );
      const formattedDateTime = utcDateTime.toISOString();

      const selectedPanel =
        editableFields.select_interviewer === "Third Party Interviewer"
          ? selectedTpInterviewer
          : editableFields.interview_panel;

      const params = new URLSearchParams({
        interview_id: interview.interview_id,
        organization_id: organizationId,
        Hr_id: hrId,
        candidate_id: interview.candidate_id,
        candidate_name: interview.candidate_name,
        job_title: interview.job_title,
        Interview_panel: selectedPanel,
        link: meetingLink,
        zoom_meeting_id: meetingId,
        round: editableFields.round,
        select_interviewer: editableFields.select_interviewer,
        Key_notes: editableFields.key_notes,
        interview_duration: editableFields.interview_duration,
        Reschedule: editableFields.reschedule,
      });

      const url = `/update_interview?${params.toString()}`;
      const requestBody = {
        status: "Active",
        interview_date_and_time: formattedDateTime,
      };

      const response = await aiDishaApi.put(url, requestBody);
      toast.success("Interview updated successfully");
      if (onUpdate) onUpdate();
      handleClose();
    } catch (error) {
      console.error("Error updating interview:", error);
      toast.error(
        "Failed to update interview: " + (error.message || "Unknown error")
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`modal fade ${isOpen ? "show" : ""}`}
      tabIndex="-1"
      style={{
        display: isOpen ? "block" : "none",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
      }}
    >
      {/* <div className="modal-dialog modal-xl modal-dialog-centered"> */}
      <div className="modal-dialog modal-xl modal-dialog-centered view-modal-shift">
        <div
          className="modal-content"
          style={{ backgroundColor: "white", position: "relative" }}
        >
          {(isLoading || isGeneratingZoom) && (
            <>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  zIndex: 999,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1000,
                }}
              >
                <Loader />
              </div>
            </>
          )}
          <div className="modal-header border-0">
            <label style={{ color: "#006A71", fontSize: "18px" }}>
              {title || "Candidate Performance"}
            </label>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
            ></button>
          </div>
          <div
            style={{
              padding: "20px",
              height: "calc(100vh - 200px)",
              overflowY: "auto",
            }}
          >
            {/* Candidate Info Row */}
            <div className="row" style={{ margin: "0% 2%" }}>
              <div className="col-sm-4">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Candidate Name
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Candidate Name"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  readOnly
                  value={
                    interview
                      ? interview.candidate_name
                      : candidateDetails.candidate_full_name || ""
                  }
                />
              </div>
              <div className="col-sm-4">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Interview ID
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Interview ID"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  readOnly
                  value={interview ? interview.interview_id : ""}
                />
              </div>
              <div className="col-sm-4">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Job Title
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Job Title"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  readOnly
                  value={
                    interview
                      ? interview.job_title
                      : candidateDetails.job_title || ""
                  }
                />
              </div>
            </div>

            {/* Round */}
            <div className="row" style={{ margin: "2% 2%" }}>
              <div className="col-sm-6">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Round
                </label>
                <select
                  className="form-control form-control-sm"
                  name="round"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  value={editableFields.round}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                >
                  <option value="">Select Round</option>
                  <option value="Technical Round 1">Technical Round 1</option>
                  <option value="Technical Round 2">Technical Round 2</option>
                  <option value="HR">HR</option>
                  <option value="Managerial">Managerial</option>
                </select>
              </div>

              {/* Select Interviewer Type */}
              <div className="col-sm-6">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Select Interviewer Type
                </label>
                <select
                  className="form-control form-control-sm"
                  name="select_interviewer"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  value={editableFields.select_interviewer}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                >
                  <option value="">Select Interviewer Type</option>
                  <option value="Technical">Technical</option>
                  <option value="Managerial">Managerial</option>
                  <option value="HR">HR</option>
                  <option value="Third Party Interviewer">
                    Third Party Interviewer
                  </option>
                </select>
              </div>
            </div>

            {/* Key Notes */}
            <div className="row" style={{ margin: "2% 2%" }}>
              <div className="col-sm-12">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Key Notes
                </label>
                <textarea
                  className="form-control form-control-sm"
                  placeholder="Key Notes"
                  name="key_notes"
                  value={editableFields.key_notes}
                  onChange={handleInputChange}
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  disabled={isReadOnly}
                  rows="3"
                />
              </div>
            </div>

            {/* Interview Duration */}
            <div className="row" style={{ margin: "2% 2%" }}>
              <div className="col-sm-6">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Interview Duration (minutes)
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  name="interview_duration"
                  placeholder="Duration in minutes"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  value={editableFields.interview_duration}
                  onChange={handleInputChange}
                  min="1"
                  disabled={isReadOnly}
                />
              </div>

              {/* Organization ID - keep for now */}
              <div className="col-sm-6">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Organization ID
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Organization ID"
                  value={interview ? interview.organization_id : ""}
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  readOnly
                />
              </div>
            </div>

            {/* Date and Time Section */}
            <div className="row" style={{ margin: "2% 2%" }}>
              <div className="col-sm-12">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                    marginBottom: "5px",
                    display: "block",
                  }}
                >
                  Interview Date and Time
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    backgroundColor: "#006A71",
                    padding: "10px",
                    borderRadius: "4px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        color: "white",
                        fontSize: "10px",
                        marginBottom: "5px",
                        display: "block",
                      }}
                    >
                      Date
                    </label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date) => {
                        !isReadOnly && setSelectedDate(date);
                        !isReadOnly && setHasChangedDate(true);
                      }}
                      dateFormat="yyyy/MM/dd"
                      placeholderText="Select a date"
                      minDate={getCurrentDateTime()}
                      disabled={isReadOnly}
                      style={{
                        backgroundColor: "transparent",
                        color: "white",
                        border: "none",
                        width: "100%",
                      }}
                      className="form-control form-control-sm"
                      wrapperClassName="date-picker-wrapper"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        color: "white",
                        fontSize: "10px",
                        marginBottom: "5px",
                        display: "block",
                      }}
                    >
                      Time
                    </label>
                    <DatePicker
                      selected={selectedTime}
                      onChange={(time) => {
                        !isReadOnly && setSelectedTime(time);
                        !isReadOnly && setHasChangedTime(true);
                      }}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      timeFormat="HH:mm"
                      dateFormat="HH:mm"
                      placeholderText="Select a time"
                      disabled={isReadOnly}
                      style={{
                        backgroundColor: "transparent",
                        color: "white",
                        border: "none",
                        width: "100%",
                      }}
                      className="form-control form-control-sm"
                      wrapperClassName="date-picker-wrapper"
                    />
                  </div>
                </div>
                {selectedDate && selectedTime && (
                  <p
                    style={{
                      color: "#033538",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    Selected Interview Time (IST):{" "}
                    {new Date(
                      selectedDate.getFullYear(),
                      selectedDate.getMonth(),
                      selectedDate.getDate(),
                      selectedTime.getHours(),
                      selectedTime.getMinutes()
                    ).toLocaleString("en-US", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </p>
                )}
                {isGeneratingZoom && (
                  <p
                    style={{
                      color: "#007bff",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    🔄 Generating Zoom meeting link...
                  </p>
                )}
              </div>
            </div>

            {/* Meeting Link and ID */}
            <div className="row" style={{ margin: "2% 2%" }}>
              <div className="col-sm-6">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Meeting Link
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="meeting_link"
                  placeholder="Meeting Link"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  value={meetingLink}
                  readOnly
                />
              </div>
              <div className="col-sm-6">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Meeting ID
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Meeting ID"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  value={meetingId}
                  readOnly
                />
              </div>
            </div>

            {/* Interviewer Selection */}
            {editableFields.select_interviewer && (
              <div className="row" style={{ margin: "2% 2%" }}>
                <div className="col-sm-12">
                  <label
                    style={{
                      fontFamily: "poppins",
                      fontSize: "12px",
                      color: "#033538",
                    }}
                  >
                    {editableFields.select_interviewer ===
                    "Third Party Interviewer"
                      ? "Select Third Party Interviewer"
                      : "Select Interviewer"}
                  </label>
                  <TableContainer
                    component={Paper}
                    style={{
                      marginTop: "10px",
                      borderRadius: "10px",
                      maxHeight: "300px",
                      overflow: "auto",
                    }}
                  >
                    <Table size="small">
                      <TableHead style={{ backgroundColor: "#033538" }}>
                        <TableRow>
                          {editableFields.select_interviewer ===
                          "Third Party Interviewer" ? (
                            <>
                              <TableCell style={{ color: "white" }}>
                                Name
                              </TableCell>
                              <TableCell style={{ color: "white" }}>
                                Email
                              </TableCell>
                              <TableCell style={{ color: "white" }}>
                                Mobile
                              </TableCell>
                              <TableCell style={{ color: "white" }}>
                                Status
                              </TableCell>
                              <TableCell style={{ color: "white" }}>
                                Action
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell style={{ color: "white" }}>
                                Name
                              </TableCell>
                              <TableCell style={{ color: "white" }}>
                                Email
                              </TableCell>
                              <TableCell style={{ color: "white" }}>
                                Skills
                              </TableCell>
                              <TableCell style={{ color: "white" }}>
                                Designation
                              </TableCell>
                              <TableCell style={{ color: "white" }}>
                                Action
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editableFields.select_interviewer ===
                        "Third Party Interviewer"
                          ? tpInterviewers.map((tp) => (
                              <TableRow
                                key={tp.user_id}
                                style={{ backgroundColor: "#006A71" }}
                              >
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {tp.name}
                                </TableCell>
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {tp.email}
                                </TableCell>
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {tp.mobile}
                                </TableCell>
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {tp.activation_status}
                                </TableCell>
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {!isReadOnly && (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() =>
                                        handleSelectTpInterviewer(tp.user_id)
                                      }
                                      style={{
                                        backgroundColor: "#033538",
                                        color: "white",
                                        "&:hover": {
                                          backgroundColor: "#02484b",
                                        },
                                        fontFamily: "Poppins",
                                        textTransform: "none",
                                      }}
                                    >
                                      Select
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          : interviewerList.map((interviewer) => (
                              <TableRow
                                key={interviewer.panelist_id}
                                style={{ backgroundColor: "#006A71" }}
                              >
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {interviewer.full_name}
                                </TableCell>
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {interviewer.official_email_id || "N/A"}
                                </TableCell>
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {Array.isArray(interviewer.skills)
                                    ? interviewer.skills.join(", ")
                                    : interviewer.skills || "N/A"}
                                </TableCell>
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {interviewer.designation || "N/A"}
                                </TableCell>
                                <TableCell
                                  style={{
                                    color: "white",
                                    border: "1px solid black",
                                  }}
                                >
                                  {!isReadOnly && (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() =>
                                        handleSelectInterviewer(
                                          interviewer.panelist_id
                                        )
                                      }
                                      style={{
                                        backgroundColor: "#033538",
                                        color: "white",
                                        "&:hover": {
                                          backgroundColor: "#02484b",
                                        },
                                        fontFamily: "Poppins",
                                        textTransform: "none",
                                      }}
                                    >
                                      Select
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {(editableFields.interview_panel ||
                    selectedTpInterviewer) && (
                    <p
                      style={{
                        color: "#033538",
                        fontSize: "12px",
                        marginTop: "10px",
                      }}
                    >
                      Selected:{" "}
                      {editableFields.select_interviewer ===
                      "Third Party Interviewer"
                        ? tpInterviewers.find(
                            (i) => i.user_id === selectedTpInterviewer
                          )?.name
                        : interviewerList.find(
                            (i) =>
                              i.panelist_id === editableFields.interview_panel
                          )?.full_name}{" "}
                      (
                      {editableFields.select_interviewer ===
                      "Third Party Interviewer"
                        ? selectedTpInterviewer
                        : editableFields.interview_panel}
                      )
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Reschedule */}
            <div className="row" style={{ margin: "2% 2%" }}>
              <div className="col-sm-6">
                <label
                  style={{
                    fontFamily: "poppins",
                    fontSize: "12px",
                    color: "#033538",
                  }}
                >
                  Reschedule
                </label>
                <select
                  className="form-control form-control-sm"
                  name="reschedule"
                  style={{
                    backgroundColor: "#006A71",
                    color: "white",
                    border: "none",
                  }}
                  value={editableFields.reschedule}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>

            {/* Buttons */}
            {!isReadOnly && (
              <div className="row" style={{ margin: "4% 2% 2% 2%" }}>
                <div className="col-sm-6">
                  <button
                    type="button"
                    className="btn btn-sm rounded-2 fw-medium"
                    onClick={handleReset}
                    style={{
                      margin: "0",
                      color: "#1E1E1E",
                      width: "100%",
                      border: "1px solid #1E1E1E",
                      backgroundColor: "#FFC107",
                      cursor: "pointer",
                    }}
                  >
                    Reset
                  </button>
                </div>
                <div className="col-sm-6">
                  <button
                    type="button"
                    className="btn btn-sm rounded-2 fw-medium"
                    onClick={handleSubmit}
                    style={{
                      margin: "0",
                      color: "#CECECE",
                      width: "100%",
                      border: "1px solid #1E1E1E",
                      backgroundColor: "#28A745",
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            )}
          </div>
          <style jsx>{`

          .view-modal-shift {
    margin-left: 350px; /* ✅ adjust if needed */
    margin-top: 55px;
  }

  @media (max-width: 992px) {
    .view-modal-shift {
      margin-left: 0; /* ✅ mobile safe */
    }
  }


            .date-picker-wrapper .react-datepicker__input-container input {
              color: white !important;
              background-color: transparent !important;
            }
            .date-picker-wrapper
              .react-datepicker__input-container
              input:disabled {
              color: #cccccc !important;
              background-color: transparent !important;
              opacity: 1 !important;
            }
            .date-picker-wrapper
              .react-datepicker__input-container
              input::placeholder {
              color: #888888 !important;
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

export default ViewModal;
